import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { useAnalytics } from '@/hooks/analytics';
import { useApi } from '@/hooks/api';

import { classifyTruemedError, startTruemedCheckout, TruemedErrorKind, TruemedPlan } from './truemedApi';

export const ZIP_PATTERN = /^\d{5}(-\d{4})?$/;

export type DisplayedTruemedError = Exclude<TruemedErrorKind, 'not_enabled'>;

// The buyer's email and ad attribution must not travel to Truemed in the return URL.
const isStrippedParam = (name: string): boolean => name === 'email' || name.startsWith('utm_');

const getFailurePath = (): string => {
	const params = new URLSearchParams(window.location.search);
	for (const name of [...params.keys()]) {
		if (isStrippedParam(name)) params.delete(name);
	}
	const search = params.toString();
	return search ? `${window.location.pathname}?${search}` : window.location.pathname;
};

export type TruemedSelection = {
	isOpen: boolean;
	isStarting: boolean;
	postalCode: string;
	isZipValid: boolean;
	showZipError: boolean;
	error: DisplayedTruemedError | null;
	zipInputRef: RefObject<HTMLInputElement | null>;
	open: () => void;
	close: () => void;
	changePostalCode: (value: string) => void;
	revealZipError: () => void;
	start: () => Promise<void>;
};

export function useTruemedSelection({
	plan,
	freeTrial,
	analyticsPrefix,
	onOpen,
}: {
	plan: TruemedPlan | null;
	freeTrial: boolean;
	analyticsPrefix?: string;
	// Runs when the row opens, so the host can collapse its own payment method.
	onOpen?: () => void;
}): TruemedSelection {
	const { logEvent } = useAnalytics();
	const apiRequest = useApi();
	const zipInputRef = useRef<HTMLInputElement>(null);

	const [isOpen, setIsOpen] = useState(false);
	const [postalCode, setPostalCode] = useState('');
	const [showZipError, setShowZipError] = useState(false);
	const [isStarting, setIsStarting] = useState(false);
	const [error, setError] = useState<DisplayedTruemedError | null>(null);
	// State updates are async, so a fast double click would otherwise start two checkouts.
	const isStartingRef = useRef(false);

	const eventName = (name: string): string => (analyticsPrefix ? `${analyticsPrefix} : ${name}` : name);

	const trimmedPostalCode = postalCode.trim();
	const isZipValid = ZIP_PATTERN.test(trimmedPostalCode);

	// Back from Truemed can restore this page from the back-forward cache with the redirect still
	// marked in flight, which would leave the host's submit button disabled for good.
	useEffect(() => {
		const onPageShow = (event: PageTransitionEvent): void => {
			if (!event.persisted) return;
			isStartingRef.current = false;
			setIsStarting(false);
		};
		window.addEventListener('pageshow', onPageShow);
		return () => window.removeEventListener('pageshow', onPageShow);
	}, []);

	// Opening is one-way, like Stripe's accordion: clicking the open row's header leaves it open,
	// and it only closes when the host closes it because another method opened.
	const open = (): void => {
		if (isOpen) return;
		setIsOpen(true);
		logEvent({ eventName: eventName('Truemed : Option : Clicked') });
		onOpen?.();
	};

	const close = useCallback((): void => setIsOpen(false), []);

	const changePostalCode = (value: string): void => {
		setPostalCode(value);
		setShowZipError(false);
		setError(null);
	};

	const revealZipError = (): void => setShowZipError(true);

	const releaseStart = (): void => {
		isStartingRef.current = false;
		setIsStarting(false);
	};

	const start = async (): Promise<void> => {
		if (isStartingRef.current || !plan) return;
		if (!isZipValid) {
			revealZipError();
			zipInputRef.current?.focus();
			return;
		}
		isStartingRef.current = true;
		setIsStarting(true);
		setError(null);
		try {
			const { redirectUrl } = await startTruemedCheckout(apiRequest, {
				plan,
				freeTrial,
				address: { country: 'US', postalCode: trimmedPostalCode },
				failurePath: getFailurePath(),
			});
			logEvent({ eventName: eventName('Truemed : Checkout : Redirected') });
			window.location.assign(redirectUrl);
		} catch (err) {
			releaseStart();
			const kind = classifyTruemedError(err);
			// A 403 means Truemed is switched off for this buyer; offer the same fallback as an outage.
			setError(kind === 'not_enabled' ? 'unavailable' : kind);
		}
	};

	return {
		isOpen,
		isStarting,
		postalCode,
		isZipValid,
		showZipError,
		error,
		zipInputRef,
		open,
		close,
		changePostalCode,
		revealZipError,
		start,
	};
}
